import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Config } from '../../Common/Config';

/**
 * IOO-225 – PatientAuthContext
 * Provides authentication state across all patient account pages.
 * Token persisted in localStorage; auto-loaded on mount.
 */

const PatientAuthContext = createContext(null);

const LS_TOKEN = 'patient_auth_token';
const LS_ACCOUNT = 'patient_account_info';
const LS_LAST_ACTIVE = 'patient_last_active_time';
const MAX_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

function checkSessionValidityOnBoot() {
  try {
    const token = localStorage.getItem(LS_TOKEN);
    const lastActive = localStorage.getItem(LS_LAST_ACTIVE);
    if (!token) return false;

    if (!lastActive) {
      // If token exists but no timestamp is stored, expire the session
      return false;
    }

    const elapsed = Date.now() - parseInt(lastActive, 10);
    if (elapsed >= MAX_EXPIRY_MS) {
      return false;
    }

    // Refresh active timestamp on successful validation
    localStorage.setItem(LS_LAST_ACTIVE, Date.now().toString());
    return true;
  } catch {
    return false;
  }
}

export function PatientAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    if (checkSessionValidityOnBoot()) {
      return localStorage.getItem(LS_TOKEN) || null;
    } else {
      [LS_TOKEN, LS_ACCOUNT, LS_LAST_ACTIVE].forEach(k => localStorage.removeItem(k));
      return null;
    }
  });

  const [account, setAccount] = useState(() => {
    if (checkSessionValidityOnBoot()) {
      try {
        const raw = localStorage.getItem(LS_ACCOUNT);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    } else {
      return null;
    }
  });

  // IOO-372 – handoff from the guest-app Sign Up modal: after activating an
  // account there, the guest app redirects here with #access_token=... (a
  // fragment, not a query param, so it's never sent to server logs). Adopt
  // it once on boot, then hydrate the full profile and strip the fragment.
  useEffect(() => {
    const match = window.location.hash.match(/access_token=([^&]+)/);
    if (!match) return;
    const handoffToken = decodeURIComponent(match[1]);
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', cleanUrl);

    localStorage.setItem(LS_TOKEN, handoffToken);
    localStorage.setItem(LS_LAST_ACTIVE, Date.now().toString());
    setToken(handoffToken);

    fetch(`${Config.urlOpen}patient/profile`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${handoffToken}` },
    })
      .then(res => res.json())
      .then(profile => {
        const account = profile?.success && profile?.data ? profile.data : {};
        localStorage.setItem(LS_ACCOUNT, JSON.stringify(account));
        setAccount(account);
      })
      .catch(() => { });
    // Runs once on mount only — a fresh handoff always arrives on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((rawToken, accountData) => {
    localStorage.setItem(LS_TOKEN, rawToken);
    localStorage.setItem(LS_ACCOUNT, JSON.stringify(accountData));
    localStorage.setItem(LS_LAST_ACTIVE, Date.now().toString());
    setToken(rawToken);
    setAccount(accountData);
  }, []);

  const logout = useCallback(() => {
    // ── Clear all patient localStorage keys ────────────────────
    [LS_TOKEN, LS_ACCOUNT, LS_LAST_ACTIVE].forEach(k => localStorage.removeItem(k));

    // ── Clear all patient sessionStorage keys ──────────────────
    [
      'patient_otp_mobile',
      'patient_otp_message_id',
      'patient_login_email',
      'patient_redirect_after_login',
      'pa_prefill',
    ].forEach(k => sessionStorage.removeItem(k));

    setToken(null);
    setAccount(null);
  }, []);

  // Activity listeners to update active timestamp and run background checks
  useEffect(() => {
    if (!token) return;

    const refreshActivity = () => {
      try {
        localStorage.setItem(LS_LAST_ACTIVE, Date.now().toString());
      } catch (_) {}
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, refreshActivity));

    // Inactivity interval check every 10 seconds to trigger logout
    const interval = setInterval(() => {
      try {
        const lastActive = localStorage.getItem(LS_LAST_ACTIVE);
        if (lastActive) {
          const elapsed = Date.now() - parseInt(lastActive, 10);
          if (elapsed >= MAX_EXPIRY_MS) {
            logout();
          }
        } else {
          logout();
        }
      } catch (_) {
        logout();
      }
    }, 10000);

    return () => {
      events.forEach(e => window.removeEventListener(e, refreshActivity));
      clearInterval(interval);
    };
  }, [token, logout]);

  // Sync logout across tabs/windows of the same browser via storage event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === LS_TOKEN && !e.newValue) {
        logout();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [logout]);

  // Verify token validity against server periodically and on visibility changes
  useEffect(() => {
    if (!token) return;

    const verifySessionOnServer = async () => {
      try {
        const res = await fetch(`${Config.urlOpen}patient/profile`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.status === 401) {
          logout();
        }
      } catch (_) {
        // Ignore network / offline errors to prevent false logouts
      }
    };

    // Verify immediately on tab/window visibility focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifySessionOnServer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Verify periodically every 30 seconds
    const verificationInterval = setInterval(verifySessionOnServer, 30000);

    // Initial validation check on load
    verifySessionOnServer();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(verificationInterval);
    };
  }, [token, logout]);

  const updateAccount = useCallback((data) => {
    const updated = { ...account, ...data };
    localStorage.setItem(LS_ACCOUNT, JSON.stringify(updated));
    setAccount(updated);
  }, [account]);

  const isAuthenticated = Boolean(token && account);

  // Perf: without this, every re-render of the provider (e.g. from the
  // interval-driven effects above) creates a new context value object,
  // which re-renders every consumer down the patient-account tree even
  // when token/account are unchanged.
  const value = useMemo(
    () => ({ token, account, isAuthenticated, login, logout, updateAccount }),
    [token, account, isAuthenticated, login, logout, updateAccount]
  );

  return (
    <PatientAuthContext.Provider value={value}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const ctx = useContext(PatientAuthContext);
  if (!ctx) throw new Error('usePatientAuth must be used inside <PatientAuthProvider>');
  return ctx;
}
